import { Alert, Button, Card, CardBody, CardHeader, Pill } from '@hvac/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { connectorCatalogQuery, syncConnector } from '../../shared/api/connection-queries.js';
import { connectionLine, syncSentence } from './connection-text.js';
import { ConnectorMark } from './connector-mark.js';

/**
 * What this business is connected to, in a card: for Settings, where people
 * look first for "what is hooked up", with the way to the full page.
 */
export function ConnectionsCard({ organizationId }: { organizationId: string }) {
  const navigate = useNavigate();
  const catalog = useQuery(connectorCatalogQuery(organizationId));
  const connected = (catalog.data?.connectors ?? []).filter(
    (entry) => entry.connection !== null && entry.connection.status !== 'disconnected',
  );

  return (
    <Card>
      <CardHeader
        title="Connections"
        description="The CRM and other systems this business's jobs and invoices come from."
        actions={
          <Button
            variant="secondary"
            size="sm"
            onClick={() => void navigate({ to: '/connections' })}
          >
            {connected.length === 0 ? 'Connect a system' : 'Manage'}
          </Button>
        }
      />
      <CardBody className="flex flex-col gap-3">
        {catalog.isPending ? null : connected.length === 0 ? (
          <p className="text-sm text-content-muted">
            Nothing connected yet. Housecall Pro and Jobber connect directly; any other CRM comes in
            as a spreadsheet export.
          </p>
        ) : (
          connected.map((entry) =>
            entry.connection === null ? null : (
              <div key={entry.provider} className="flex items-center gap-3">
                <ConnectorMark kind={entry.provider} size="sm" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-content">{entry.name}</p>
                  <p className="truncate text-xs text-content-muted">
                    {connectionLine(entry.connection)}
                  </p>
                </div>
                {entry.connection.status === 'failing' ? (
                  <Pill tone="warning">Needs attention</Pill>
                ) : (
                  <Pill tone="success">Connected</Pill>
                )}
              </div>
            ),
          )
        )}
      </CardBody>
    </Card>
  );
}

/**
 * Sync every connected system, for pages (Customers, Jobs) where the person
 * wants today's work in now: a button for the page header and the message it
 * leaves. With nothing connected, the button points at Connections.
 */
export function useSyncConnections(organizationId: string) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const catalog = useQuery(connectorCatalogQuery(organizationId));
  const [message, setMessage] = useState<{ tone: 'success' | 'danger'; text: string } | null>(null);
  const connected = (catalog.data?.connectors ?? []).filter(
    (entry) => entry.connection !== null && entry.connection.status !== 'disconnected',
  );

  const sync = useMutation({
    mutationFn: async () => {
      const sentences: string[] = [];
      for (const entry of connected) {
        sentences.push(
          syncSentence(entry.name, await syncConnector(organizationId, entry.provider)),
        );
      }
      return sentences.join(' ');
    },
    onSuccess: (text) => {
      setMessage({ tone: 'success', text });
      void queryClient.invalidateQueries();
    },
    onError: (caught) =>
      setMessage({
        tone: 'danger',
        text: caught instanceof Error ? caught.message : 'The sync did not run.',
      }),
  });

  return {
    button:
      connected.length === 0 ? (
        <Button variant="secondary" onClick={() => void navigate({ to: '/connections' })}>
          Connect a CRM
        </Button>
      ) : (
        <Button variant="secondary" onClick={() => sync.mutate()} isLoading={sync.isPending}>
          Sync {connected.length === 1 ? (connected[0]?.name ?? '') : 'everything'}
        </Button>
      ),
    message: message === null ? null : <Alert tone={message.tone}>{message.text}</Alert>,
  };
}
